import {
  useMutation as orgUseMutation,
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import isEmpty from "lodash-es/isEmpty";
import { TlxHttpResponse } from "@/api";
import getMock from "@/configs/mock";
import { getMockData } from "@/helpers/getMockData";
import { useIsInMockScope } from "@/libs/mockScope";

export type MutationMethod<Vars, Response> = (vars: Vars) => Promise<Response>;

export type MutationOptions<Vars, Response> = UseMutationOptions<
  Response,
  AxiosError<TlxHttpResponse>,
  Vars
>;

export interface MutationProps<Vars, Response> {
  method: MutationMethod<Vars, Response>;
  options?: MutationOptions<Vars, Response>;
}

type MutationResultWithLegacyLoading<Vars, Response> = UseMutationResult<
  Response,
  AxiosError<TlxHttpResponse>,
  Vars,
  unknown
> & { isLoading: boolean };

function useMutationImpl<Vars, Response>(
  args: MutationProps<Vars, Response>,
): MutationResultWithLegacyLoading<Vars, Response> {
  const { method, options = {} } = args;
  const isInMockScope = useIsInMockScope();

  const mutationFn = (variables: Vars) => {
    const isMock = getMock(
      method as Parameters<typeof getMock>[0],
      variables,
      isInMockScope,
    );
    return !isEmpty(isMock)
      ? Promise.resolve(getMockData<Vars, Response>(method, variables))
      : method(variables);
  };

  const result = orgUseMutation<Response, AxiosError<TlxHttpResponse>, Vars>({
    mutationFn,
    ...options,
  });

  return {
    ...result,
    isLoading: result.isPending,
  };
}

function useMutation<Vars, Response>(
  props: MutationProps<Vars, Response>,
): MutationResultWithLegacyLoading<Vars, Response>;
function useMutation<Vars, Response>(
  method: MutationMethod<Vars, Response>,
  options?: MutationOptions<Vars, Response>,
): MutationResultWithLegacyLoading<Vars, Response>;
function useMutation<Vars, Response>(
  methodOrProps: MutationMethod<Vars, Response> | MutationProps<Vars, Response>,
  maybeOptions?: MutationOptions<Vars, Response>,
): MutationResultWithLegacyLoading<Vars, Response> {
  const args =
    typeof methodOrProps === "function"
      ? { method: methodOrProps, options: maybeOptions ?? {} }
      : methodOrProps;
  return useMutationImpl(args);
}

export default useMutation;
